import React, { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainHome from "../../MainHome";
import InfoFieldsBuilder, {
  InfoFieldsBuilderInterface,
} from "../InfoFieldsBuilder";
import "../../../styles/components/generalInformation.scss";
import { ReactComponent as BatteryIcon } from "../../../common/assats/icons/batteryGreen.svg";
import { canAccessRoute } from "../../../common/function/roleRoutePermissions";
import useUserStore from "../../../common/store/user.store";
import { getBatteryStatus } from "../../../common/api/apiCalls";


type InformationDataType = {
  displayData: InfoFieldsBuilderInterface[];
  editState: any;
  informationData: {
    title: string;
    status: string;
    batteryStatus?: string;
    textArray: string[];
    icon?: string;
    pathToEdit: string;
    editPermission: string;
    userToEditRole: string;
  };
};

const GeneralInformation: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isEditDisabled, setIsEditDisabled] = React.useState(true);
  const [batteryStatus, setBatteryStatus] = React.useState<string | undefined>(
    undefined
  );
  const { user, getUser, canAccessCRUD } = useUserStore();

  const locationState = location.state as InformationDataType;
  const informationData = locationState?.informationData;
  const data = locationState?.displayData;

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (informationData && data) {
      setIsAddDisabledState();
    }
  }, [location]);

  useEffect(() => {
    // Only poll for battery status on device pages
    const isDevicePage = location.pathname.includes("/Devices/View/");
    
    // Get deviceId from route params or location state
    const deviceId = id || locationState?.editState?.id;
    
    if (!deviceId || !isDevicePage) return;

    // Set initial battery status from location state if available
    if (informationData?.batteryStatus) {
      setBatteryStatus(informationData.batteryStatus);
    }

    // Function to fetch battery status
    const fetchBatteryStatus = async () => {
      try {
        const response = await getBatteryStatus(deviceId);
        if (response.success && response.data?.batteryStatus !== undefined) {
          setBatteryStatus(String(response.data.batteryStatus));
        }
      } catch (error) {
        // Silently handle errors to avoid disrupting the UI
        console.error("Failed to fetch battery status:", error);
      }
    };

    // Initial fetch
    fetchBatteryStatus();

    // Set up polling every 5 seconds
    pollingIntervalRef.current = setInterval(() => {
      fetchBatteryStatus();
    }, 30000);

    // Cleanup function to stop polling when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [id, location.pathname, locationState?.editState?.id, informationData?.batteryStatus]);

  const setIsAddDisabledState = async () => {
    let user = await getUser();
    const role = user?.role;
    if (!role) return;
    const path = (informationData && informationData.editPermission) || "";
    const hasAccess = canAccessRoute(role, path);

    const hasRoleOprationAccess = canAccessCRUD(path, role);

    if (hasAccess && hasRoleOprationAccess) {
      setIsEditDisabled(false);
    }
  };

  const editOnClick = () => {
    navigate(informationData.pathToEdit, { state: locationState.editState });
  };

  return (
    <MainHome>
      <div className="general-container">
        <div className="general-header">
          <div className="header-content">
            <div className="device-title-container">
              <label className="device-title">{informationData.title}</label>
              {isEditDisabled ? null : (
                <span className="edit-link" onClick={editOnClick}>
                  {t("general.edit")}
                </span>
              )}
            </div>
           
            <div className="status-badges-row">
              {informationData.status && (
                <div className="status-badge">{informationData.status}</div>
              )}
              {(batteryStatus !== undefined || locationState.informationData?.batteryStatus) && (
                <div className="battery-badge">
                  <BatteryIcon className="battery-icon" />
                  <span className="battery-percentage">
                    {batteryStatus !== undefined ? batteryStatus : informationData?.batteryStatus}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="information-content">
          <InfoFieldsBuilder fields={data} />
        </div>
      </div>
    </MainHome>
  );
};

export default GeneralInformation;
